import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ViewGeneratedReportComponent } from './view-generated-report/view-generated-report.component';
import { SharedService } from 'src/app/services/shared.service';
import { GenerateNewReportComponent } from './generate-new-report/generate-new-report.component';
import { Conversation } from '../../models/conversation.model';

@Component({
    selector: 'app-report-generation',
    templateUrl: './report-generation.component.html',
    styleUrl: './report-generation.component.scss',
    standalone: false
})
export class ReportGenerationComponent implements OnInit, OnChanges {
  @ViewChild('viewReport') reportView!: ViewGeneratedReportComponent;
  @ViewChild('generateNewReport') generateNewReportModal!: GenerateNewReportComponent;
  @ViewChild('responseModal') responseModal!: TemplateRef<any>;
  @Output() updateReportsEvent = new EventEmitter<any>()
  @Output() reportOpened = new EventEmitter<void>();

  @Input() allReports: any;
  @Input() page_source!: string;
	@Input() page_data!: any;
  @Input() entity!: any;
  @Input() autoOpenReportId: string | null = null;

  // Data Source
  // allReports: any[] = [];
  reports: any[] = [];

  get_report_url: string = '';
  generate_report_url: string = '';
  delete_report_url: string = '';
  get_entity_data_url: string = '';
  activity_type: string = '';

  // Pagination Settings
  currentPage = 1;
  pageSize = 4;
  startIndex = 1;
  endIndex = 4;

  video_context: string = '';

  submitted = false;
  spinner = false;
  success = false;
  message: string = '';

  selectedReport: any = null;
  deleteModalRef: any;

  constructor(private modalService: NgbModal, private sharedService: SharedService) { }

  async ngOnInit() {
    await this.build_url();
    this.getReports();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allReports']) {
      this.getReports();
    }

    if (this.autoOpenReportId && this.allReports && this.allReports.length > 0) {
      this.checkAndOpenReport(this.autoOpenReportId);
    }
  }

  checkAndOpenReport(id: string) {
    const reportToOpen = this.allReports.find((r: any) => r.id === id); 
    
    setTimeout(() => {
      if (reportToOpen) {
        this.openViewReport(reportToOpen);      
        this.reportOpened.emit(); 
      } else {
        console.warn(`Report with ID ${id} was not found in allReports.`);
      }
    }, 100);
  }

  openGenerateNewReportModal() {
		this.generateNewReportModal.openModal(this.entity.extra_content.analyze_version);
	}

  openViewTextModal(content: any, video_context: string) {
    this.video_context = video_context;
    this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
  }

  openViewReport(report: any) {
    this.reportView.openModal(report, this.get_report_url, this.activity_type, this.generate_report_url, this.get_entity_data_url)
  }

  isRtl(text: string): boolean {
    if (!text) return false;
    const rtlPattern = /[\u0590-\u06FF]/;
    return rtlPattern.test(text);
  }

  updateReports(data: any) {
    this.updateReportsEvent.emit(data);
  }

  openDeleteReportModal(report: any, content: any) {
    this.selectedReport = report;
    this.deleteModalRef = this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
  }

  deleteReport() {
		this.submitted = true;
		this.spinner = true;

    this.sharedService.deleteReport(`${this.delete_report_url}/${this.selectedReport.id}`, this.activity_type).subscribe({
			next: (res) => {
				this.success = true;
				this.submitted = false;
				this.spinner = false;
        this.entity = res.entity;
				this.message = res.message;
				this.deleteModalRef.close();
				this.modalService.open(this.responseModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.message = err.message ? err.message : err;
				this.deleteModalRef.close();
				this.modalService.open(this.responseModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
  }

  async closeModal() {
		if (this.success) {
			this.updateReportsEvent.emit({ entity: this.entity });
		}
		
		this.modalService.dismissAll();
	}

  getReports() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = this.currentPage * this.pageSize;
    this.reports = this.allReports.slice(start, end);
    this.startIndex = start + 1;
    this.endIndex = Math.min(end, this.allReports.length);
  }

  async build_url() {
    switch (this.page_source) {
      case 'conversation':
        this.activity_type = 'conversation';
        this.get_report_url = `conversation/get-report/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        this.generate_report_url = `conversation/generate-report/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        this.delete_report_url = `conversation/delete-report/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        this.get_entity_data_url = `conversation/${this.page_data.project_id}/${this.page_data.conversation_id}`;
        break;
      case 'case':
        this.activity_type = 'conversation';
        this.get_report_url = `case/get-report/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        this.generate_report_url = `case/generate-report/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        this.delete_report_url = `case/delete-report/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        this.get_entity_data_url = `case/${this.page_data.project_id}/${this.page_data.case_id}/${this.page_data.conversation_id}`;
        break;
      case 'examinee':
        this.activity_type = 'examinee';
        this.get_report_url = `examinee/get-report/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        this.generate_report_url = `examinee/generate-report/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        this.delete_report_url = `examinee/delete-report/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        this.get_entity_data_url = `examinee/details/${this.page_data.project_id}/${this.page_data.examinee_id}`;
        break;
      case 'case_examinee':
        this.activity_type = 'examinee';
        this.get_report_url = `case/get-report/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        this.generate_report_url = `case/generate-report/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        this.delete_report_url = `case/delete-report/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        this.get_entity_data_url = `case/${this.page_data.project_id}/${this.page_data.assessment_id}/${this.page_data.examinee_id}`;
        break;
    
      default:
        break;
    }
  }
}
